/**
 * IDLE DETECTION — pure logic for "the timer kept running while the user was
 * away; what do we do with the idle stretch?".
 *
 * The threshold is a LOCAL preference (device-scoped localStorage, same class as
 * the weekly goal / timesheet rate fallback), so it lives outside PowerSync.
 * Everything here is PURE and deterministic: the caller feeds timestamps and gets
 * back the idle snapshot / resolution the UI applies. No I/O, no hidden
 * `Date.now()` reads, so it is fully unit-testable.
 *
 * Semantics (Toggl-style): the idle stretch starts at the LAST user activity, not
 * at the moment the threshold fired. "Discarding" the idle time therefore
 * truncates the running entry back to that last-activity instant (clamped so an
 * entry never ends before it starts).
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

export const MS_PER_MINUTE = SECONDS_PER_MINUTE * MS_PER_SECOND;

/** Upper bound for the idle threshold: 4 hours. */
export const MAX_IDLE_THRESHOLD_MINUTES = 240;

/** localStorage key holding the device-scoped idle threshold (minutes, string). */
export const IDLE_THRESHOLD_STORAGE_KEY = "idle_threshold_minutes";

/** Clamp any input to a valid threshold in [0, MAX]; NaN/negative → 0 (= off). */
export function clampIdleThresholdMinutes(value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }
  return value > MAX_IDLE_THRESHOLD_MINUTES ? MAX_IDLE_THRESHOLD_MINUTES : value;
}

/** Parse a stored/user string into a clamped threshold (invalid/empty → 0). */
export function parseIdleThresholdMinutes(raw: string | null | undefined): number {
  if (raw === null || raw === undefined || raw.trim() === "") {
    return 0;
  }
  return clampIdleThresholdMinutes(Number.parseFloat(raw));
}

export interface IdleState {
  /** True once the threshold is enabled (>0) and fully elapsed without activity. */
  isIdle: boolean;
  /** Epoch ms where the idle stretch began (= last activity); null when not idle. */
  idleStartAt: number | null;
  /** Milliseconds of idleness accumulated so far (0 when not idle). */
  idleMs: number;
}

export interface ComputeIdleStateArgs {
  /** Epoch ms of the last observed user activity. */
  lastActivityAt: number;
  /** Epoch ms of "now". */
  now: number;
  /** Threshold in minutes; <=0 disables detection entirely. */
  thresholdMinutes: number;
}

/**
 * Snapshot of the idle state. Pure: same inputs, same output. A negative elapsed
 * (clock skew / lastActivity in the future) never reports idle.
 */
export function computeIdleState(args: ComputeIdleStateArgs): IdleState {
  const thresholdMinutes = clampIdleThresholdMinutes(args.thresholdMinutes);
  if (thresholdMinutes <= 0) {
    return { isIdle: false, idleStartAt: null, idleMs: 0 };
  }
  const elapsedMs = args.now - args.lastActivityAt;
  if (!Number.isFinite(elapsedMs) || elapsedMs < thresholdMinutes * MS_PER_MINUTE) {
    return { isIdle: false, idleStartAt: null, idleMs: 0 };
  }
  return { isIdle: true, idleStartAt: args.lastActivityAt, idleMs: elapsedMs };
}

/** What the user chose to do with the idle stretch. */
export type IdleAction = "keep" | "discard_stop" | "discard_resume";

export interface IdleResolution {
  /**
   * ISO instant the running entry must be stopped at (its new `end_time`), or
   * null when the entry is left untouched ("keep").
   */
  stopAtIso: string | null;
  /** True when a fresh running entry should be started "now" (split/resume). */
  resume: boolean;
}

export interface ResolveIdleActionArgs {
  /** `start_time` of the running entry (ISO); used to clamp the truncation. */
  entryStartIso: string | null;
  /** Epoch ms where the idle stretch began (from {@link computeIdleState}). */
  idleStartAt: number;
}

/**
 * End instant for a discarded idle stretch: the idle start, clamped so it never
 * precedes the entry's own start (a zero-length entry is the worst case, never a
 * negative one). An unparsable/missing entry start falls back to the idle start.
 */
export function clampIdleEndMs(entryStartIso: string | null, idleStartAt: number): number {
  if (!entryStartIso) {
    return idleStartAt;
  }
  const entryStartMs = Date.parse(entryStartIso);
  if (Number.isNaN(entryStartMs)) {
    return idleStartAt;
  }
  return Math.max(entryStartMs, idleStartAt);
}

/** Resolve a user choice into the write the caller must apply. Pure. */
export function resolveIdleAction(action: IdleAction, args: ResolveIdleActionArgs): IdleResolution {
  if (action === "keep") {
    return { stopAtIso: null, resume: false };
  }
  const stopAtIso = new Date(clampIdleEndMs(args.entryStartIso, args.idleStartAt)).toISOString();
  return { stopAtIso, resume: action === "discard_resume" };
}

/** Whole minutes of idleness for display, always at least 1 once idle. */
export function idleMinutesForDisplay(idleMs: number): number {
  if (!Number.isFinite(idleMs) || idleMs <= 0) {
    return 0;
  }
  return Math.max(1, Math.round(idleMs / MS_PER_MINUTE));
}
